import React, { FC, useCallback, useState, useMemo } from 'react'
import DocumentTitle from 'react-document-title'
import debounce from 'lodash/debounce'
import { Button } from 'transfix-ui/components/Button'
import { IFormModalProps } from 'transfix-ui/components/Modals'
import { Spinner } from 'transfix-ui/components/Spinner'
import { TextField } from 'transfix-ui/components/TextField'
import { Grid, Box } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { generatePageTitle } from 'utils/generateAppDefaults'
import { useIssuesContext } from '../IssuesContext'
import IssuesWrap from '../IssuesWrap'
import ControlPanelIssueTypeModal, { MODAL_TYPE } from './ControlPanelIssueTypeModal'
import ControlPanelTable from './ControlPanelTable'
import useIssueTypeModal from './hooks/useIssueTypeModal'
import filterIssueTypes from './utils/filterIssueTypes'

/**
 * Issues Management: Control Panel
 * @route /issues/control-panel
 */
const ControlPanel: FC = () => {
  const { data, loadingIssueTypes } = useIssuesContext()
  const [searchTerm, setSearchTerm] = useState<String>('')
  const issueTypes = useMemo(() => filterIssueTypes(data.types, searchTerm, data.categories), [
    searchTerm,
    data,
  ])
  const {
    isModalOpen,
    issueTypeId,
    modalType,
    selectedIssueType,
    toggleIssueTypeModal,
  } = useIssueTypeModal(issueTypes)
  const handleCreateIssueType = useCallback(() => toggleIssueTypeModal('new'), [
    toggleIssueTypeModal,
  ])
  const handleInputChange = debounce(setSearchTerm, 200)

  return (
    <Box px={4} pt={2}>
      <Box mb={4} display='flex' alignItems='center'>
        <Typography variant='h2'>Shipment Issue Control Panel</Typography>
      </Box>
      <Box mb={5} display='flex' alignItems='center' justifyContent='space-between'>
        <Grid item xs={6} md={2}>
          <TextField
            type='text'
            placeholder='Search Issue Types'
            dataTest='search-input'
            icon='search'
            onChange={event => handleInputChange(event.target.value)}
          />
        </Grid>
        <Button
          onClick={handleCreateIssueType}
          color='primary'
          id='new-issue-type'
          data-test='new-issue-type'
          size='small'>
          Create New Issue Type
        </Button>
      </Box>
      {loadingIssueTypes && !issueTypes.length ? (
        <Spinner position='absolute' />
      ) : (
        <ControlPanelTable toggleIssueTypeModal={toggleIssueTypeModal} issueTypes={issueTypes} />
      )}
      {isModalOpen && (
        <ControlPanelIssueTypeModal
          id={issueTypeId}
          type={modalType as MODAL_TYPE}
          open={isModalOpen}
          onClose={toggleIssueTypeModal as Extract<IFormModalProps, 'onClose'>}
          issueType={selectedIssueType}
        />
      )}
    </Box>
  )
}

const ControlPanelWrap = () => (
  <DocumentTitle title={generatePageTitle('Control Panel')}>
    <IssuesWrap>
      <ControlPanel />
    </IssuesWrap>
  </DocumentTitle>
)

export default ControlPanelWrap
